import { useState, useEffect, useRef } from 'react';
import Echo from 'laravel-echo';
import { useNavigate } from 'react-router-dom';
import Pusher from 'pusher-js'; 

function useSoketiRoom(roomId, isAuthenticated, pb, onAiResponse) {
  const navigate = useNavigate();
  const echoRef = useRef(null);
  
  const [transcript, setTranscript] = useState([]);
  const [roomMeta, setRoomMeta] = useState({ title: `Session ${roomId}`, description: '' });
  const [isHost, setIsHost] = useState(false);
  const [roomRecordId, setRoomRecordId] = useState(null);

  useEffect(() => {
    if (!roomId) {
      const randomRoom = Math.random().toString(36).substring(2, 8);
      navigate(`/room/${randomRoom}`, { replace: true });
      return;
    }
    if (!isAuthenticated) return;

    let isMounted = true;

    const initialize = async () => {
      try {
        // 1. Load Room Metadata
        const records = await pb.collection('rooms').getFullList({ filter: `roomId = "${roomId}"` });
        if (!isMounted) return;

        if (records.length > 0) {
          const room = records[0];
          setRoomMeta({ title: room.title, description: room.description });
          setRoomRecordId(room.id);
          setIsHost(room.owner === pb.authStore.record?.id);
        } else {
          const newRoom = await pb.collection('rooms').create({
            roomId, title: `Session ${roomId}`, description: 'A new podcast session.', owner: pb.authStore.record?.id
          });
          if (!isMounted) return;
          setRoomMeta({ title: newRoom.title, description: newRoom.description });
          setRoomRecordId(newRoom.id);
          setIsHost(true);
        }

        // 2. Load History
        const historyRecords = await pb.collection('transcripts').getFullList({ filter: `roomId = "${roomId}"`, sort: 'created' });
        if (!isMounted) return;
        
        setTranscript(historyRecords.map(r => ({
          speaker: r.speaker, message: r.message, timestamp: new Date(r.created).toLocaleTimeString()
        })));

        // 3. Initialize Soketi
        const echoInstance = new Echo({
          broadcaster: 'pusher',
          cluster: 'mt1',
          key: 'V2k2dkRaNHNEakNxd0tEd1ZtVnlwbnJOV3pJU09SZW50bENIRE1LWG9tWTBqNDFWeUU5Y0kyR0VSdXdmSEdwVw==',
          wsHost: 'ws.kongpodcast.com',
          wsPort: 443,
          wssPort: 443,
          forceTLS: true,
          disableStats: false,
          enabledTransports: ['ws', 'wss'],
          authorizer: (channel, options) => ({
            authorize: (socketId, callback) => {
              fetch('/pusher/auth', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': pb.authStore.isValid ? `Bearer ${pb.authStore.token}` : '',
                },
                body: JSON.stringify({ socket_id: socketId, channel_name: channel.name, username: pb.authStore.record?.name || 'Guest' }),
              })
              .then(res => {
                if (!res.ok) {
                  // If subscription is invalid/forbidden, redirect to checkout
                  if (res.status === 403 || res.status === 401) {
                    navigate(`/checkout?roomId=${roomId}`);
                  }
                  throw new Error(`Auth failed with status ${res.status}`);
                }
                return res.json();
              })
              .then(data => callback(false, data))
              .catch(err => callback(true, err));
            }
          })
        });

        if (!isMounted) { echoInstance.disconnect(); return; }
        echoRef.current = echoInstance;

        echoInstance.join(`podcast-room.${roomId}`)
          .listen('.transcript.update', (e) => {
            if (!isMounted) return;
            setTranscript(prev => {
              const isDuplicate = prev.some(item => item.timestamp === e.timestamp && item.message === e.message && item.speaker === e.speaker);
              return isDuplicate ? prev : [...prev, e];
            });
            // Handle both variation strings for AI agent responses
            if (e.speaker === 'AI Agent Atlas' || e.speaker === 'Atlas') {
              if (onAiResponse) onAiResponse(e.message);
            }
          });
      } catch (error) {
        console.error("Room connection error:", error);
      }
    };

    initialize();

    return () => {
      isMounted = false;
      if (echoRef.current) {
        echoRef.current.leave(`podcast-room.${roomId}`);
        echoRef.current.disconnect();
      }
    };
  }, [roomId, isAuthenticated, navigate, pb, onAiResponse]);

  return { transcript, roomMeta, setRoomMeta, isHost, roomRecordId };
}

export default useSoketiRoom;